const SELECTORS = {
  TRIGGER: '.js-custom-select-trigger',
  MENU: '.js-custom-select-menu',
  NATIVE: '.js-custom-select-native',
  VALUE: '.js-custom-select-value',
  OPTION: '.js-custom-select-option',
};

const CLASSES = {
  OPEN: 'is-open',
  CUSTOM_SELECT_SELECTED: 'custom-select--selected',
};

const ATTRIBUTES = {
  ARIA_EXPANDED: 'aria-expanded',
  ARIA_ACTIVEDESCENDANT: 'aria-activedescendant',
  DATA_VALUE: 'data-value',
};

class CustomSelect extends HTMLElement {
  constructor() {
    super();
    /** @type {EventListener | null} */
    this._onTriggerClick = null;
    /** @type {EventListener | null} */
    this._onTriggerKeydown = null;
    /** @type {EventListener | null} */
    this._onMenuClick = null;
    /** @type {EventListener | null} */
    this._onMenuKeydown = null;
    /** @type {EventListener | null} */
    this._onDocumentClick = null;
  }

  connectedCallback() {
    this.teardownEventListeners();
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.teardownEventListeners();
  }

  setupEventListeners() {
    const trigger = this.querySelector(SELECTORS.TRIGGER);
    const menu = this.querySelector(SELECTORS.MENU);
    const nativeSelect = this.querySelector(SELECTORS.NATIVE);
    const valueDisplay = this.querySelector(SELECTORS.VALUE);

    if (
      !(trigger instanceof HTMLButtonElement) ||
      !(menu instanceof HTMLElement) ||
      !(nativeSelect instanceof HTMLSelectElement) ||
      !(valueDisplay instanceof HTMLElement)
    ) {
      return;
    }

    /** @type {HTMLButtonElement[]} */
    const options = Array.from(this.querySelectorAll(SELECTORS.OPTION)).filter(
      /** @returns {option is HTMLButtonElement} */ (option) => option instanceof HTMLButtonElement,
    );
    let activeIndex = -1;
    const isOpen = () => menu.classList.contains(CLASSES.OPEN);
    const openMenu = () => {
      menu.classList.add(CLASSES.OPEN);
      trigger.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'true');
      if (activeIndex < 0 && options.length) {
        const selectedIndex = options.findIndex(
          (option) => option.getAttribute(ATTRIBUTES.DATA_VALUE) === nativeSelect.value,
        );
        setActive(selectedIndex >= 0 ? selectedIndex : 0);
      }
    };
    const closeMenu = (focusTrigger = false) => {
      menu.classList.remove(CLASSES.OPEN);
      trigger.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'false');
      trigger.setAttribute(ATTRIBUTES.ARIA_ACTIVEDESCENDANT, '');
      activeIndex = -1;
      if (focusTrigger) trigger.focus();
    };
    /** @param {number} index */
    const setActive = (index) => {
      if (!options.length) return;
      activeIndex = (index + options.length) % options.length;
      const activeOption = options[activeIndex];
      if (!activeOption) return;
      trigger.setAttribute(ATTRIBUTES.ARIA_ACTIVEDESCENDANT, activeOption.id || '');
      activeOption.focus();
    };
    /** @param {number} index */
    const selectAt = (index) => {
      if (!options.length) return;
      const normalized = (index + options.length) % options.length;
      const option = options[normalized];
      if (!option) return;
      const value = option.getAttribute(ATTRIBUTES.DATA_VALUE);
      const label = (option.textContent || '').trim();
      if (!value || !label) return;
      nativeSelect.value = value;
      valueDisplay.textContent = label;
      this.classList.add(CLASSES.CUSTOM_SELECT_SELECTED);
      nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    };

    this._onTriggerClick = /** @param {MouseEvent} e */ (e) => {
      e.preventDefault();
      isOpen() ? closeMenu() : openMenu();
    };
    trigger.addEventListener('click', this._onTriggerClick);

    this._onTriggerKeydown = /** @param {KeyboardEvent} e */ (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!isOpen()) {
          openMenu();
        } else {
          selectAt(activeIndex >= 0 ? activeIndex : 0);
          closeMenu(true);
        }
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        openMenu();
        setActive(activeIndex >= 0 ? activeIndex + 1 : 0);
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        openMenu();
        setActive(activeIndex >= 0 ? activeIndex - 1 : options.length - 1);
      }
      if (e.key === 'Escape' && isOpen()) {
        e.preventDefault();
        closeMenu(true);
      }
    };
    trigger.addEventListener('keydown', this._onTriggerKeydown);

    this._onMenuClick = /** @param {MouseEvent} e */ (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const option = target.closest(SELECTORS.OPTION);
      if (!(option instanceof HTMLButtonElement)) return;
      const index = options.indexOf(option);
      if (index < 0) return;
      selectAt(index);
      closeMenu(true);
    };
    menu.addEventListener('click', this._onMenuClick);

    this._onMenuKeydown = /** @param {KeyboardEvent} e */ (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const focusedOption = target.closest(SELECTORS.OPTION);
      if (!(focusedOption instanceof HTMLButtonElement)) return;
      const currentIndex = options.indexOf(focusedOption);
      if (currentIndex < 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive(currentIndex + 1);
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive(currentIndex - 1);
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        selectAt(currentIndex);
        closeMenu(true);
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu(true);
      }
    };
    menu.addEventListener('keydown', this._onMenuKeydown);

    this._onDocumentClick = /** @param {MouseEvent} e */ (e) => {
      const target = e.target;
      if (target instanceof Node && this.contains(target)) return;
      closeMenu();
    };
    document.addEventListener('click', this._onDocumentClick);
  }

  teardownEventListeners() {
    const trigger = this.querySelector(SELECTORS.TRIGGER);
    if (trigger && this._onTriggerClick) {
      trigger.removeEventListener('click', this._onTriggerClick);
    }
    if (trigger && this._onTriggerKeydown) {
      trigger.removeEventListener('keydown', this._onTriggerKeydown);
    }
    const menu = this.querySelector(SELECTORS.MENU);
    if (menu && this._onMenuClick) {
      menu.removeEventListener('click', this._onMenuClick);
    }
    if (menu && this._onMenuKeydown) {
      menu.removeEventListener('keydown', this._onMenuKeydown);
    }

    if (this._onDocumentClick) {
      document.removeEventListener('click', this._onDocumentClick);
      this._onDocumentClick = null;
    }

    this._onTriggerClick = null;
    this._onTriggerKeydown = null;
    this._onMenuClick = null;
    this._onMenuKeydown = null;
  }
}

if (!customElements.get('custom-select')) {
  customElements.define('custom-select', CustomSelect);
}
